import React from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '@/components/ui/Card/Card';
import { Button } from '@/components/ui/Button/Button';
import { Spinner } from '@/components/ui/Spinner/Spinner';
import { usePageHeader } from '@/hooks/usePageHeader';
import { PageContainer } from '@/components/ui/PageContainer';
import { AccountService } from '@/services/AccountService';
import { useNotificationStore } from '@/stores/notificationStore';
import { useAccountSessions, useRevokeSessionMutation } from '@/hooks/queries';
import { useQueryClient } from '@tanstack/react-query';
import { formatDateTimeStr } from '@/lib/localeFormatters';
import { t } from '@/lib/i18n';

export function SessionsPage() {
  usePageHeader({ title: t('Sessions'), breadcrumbs: [] });

  const svc = React.useMemo(() => new AccountService(), []);
  const queryClient = useQueryClient();
  const { data: sessions = [], isLoading: loading } = useAccountSessions();
  const revokeSessionMutation = useRevokeSessionMutation();

  const handleRevoke = async (sessionId: string) => {
    const currentTokenId = (document.cookie
      .split('; ')
      .find(row => row.startsWith('refresh_token_id='))
      ?.split('=')[1]);
    
    const isCurrentSession = sessionId === currentTokenId;
    
    try {
      await revokeSessionMutation.mutateAsync(sessionId);
      
      if (isCurrentSession) {
        // If revoking current session, logout user
        try { 
          const { useAuthStore } = await import('@/stores/authStore');
          useAuthStore.getState().logout();
          useNotificationStore.getState().info(t('You have been logged out due to session revocation'));
        } catch {}
      } else {
        try { useNotificationStore.getState().success(t('Session revoked')); } catch {}
      }
    } catch (err) {
      try { useNotificationStore.getState().error(t('Failed to revoke session')); } catch {}
    }
  };

  return (
    <PageContainer>
      <div className="space-y-6">
        {/* Info */}
      <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <h3 className="text-sm font-medium text-blue-900 mb-1">What are sessions?</h3>
        <p className="text-sm text-blue-700">
          Each session represents a login from a device or browser. Revoking a session will sign you out of that device. 
          If you revoke your current session, you'll be logged out immediately.
        </p>
      </div>

      {/* Content */}
      <Card>
        <CardHeader>
          <CardTitle>Active Sessions</CardTitle>
          <CardDescription>See your signed-in devices and revoke access</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center h-32"><Spinner /></div>
          ) : (
            <div className="space-y-3">
              {sessions.length === 0 && <div className="text-sm text-muted-foreground">No active sessions.</div>}
              {sessions.map((s: any) => (
                <div key={s.id} className="flex items-center justify-between border rounded p-3">
                  <div>
                    <div className="text-sm font-medium">{s.user_agent || t('Unknown device')}</div>
                    <div className="text-xs text-muted-foreground">IP {s.ip || 'unknown'} · Signed in {formatDateTimeStr(s.created_at)}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    {s.revoked_at ? (
                      <span className="text-xs text-muted-foreground">Revoked</span>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => handleRevoke(s.id)}>
                        Revoke
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-end">
          <Button variant="outline" onClick={async () => { try { await svc.revokeOtherSessions(); queryClient.invalidateQueries({ queryKey: ['account', 'sessions'] }); useNotificationStore.getState().success(t('Revoked all other sessions')); } catch { useNotificationStore.getState().error(t('Failed to revoke sessions')); } }}>Revoke all other sessions</Button>
        </CardFooter>
      </Card>
      </div>
    </PageContainer>
  );
}


